import { useMutation } from '@tanstack/react-query';
import {
  Box,
  Button,
  Form,
  Heading,
  Link,
  Paragraph,
  Stack,
  Text,
  TextField,
  useSnackbars,
} from '@customerio/pluma-components/react';
import { HydraUIAPIError } from '@customerio/hydra-ui-api';
import { z } from '@customerio/hydra-validation';
import { useZodForm } from '@/components/form';
import { createJoinRequest, signupResumeHref, type JoinRequestAccounts } from '@/services/join-request/api';

const schema = z.object({
  firstName: z.string().trim().min(1, 'Enter your first name.'),
  lastName: z.string().trim().min(1, 'Enter your last name.'),
});

type OrgPickerStepProps = {
  token: string;
  data: JoinRequestAccounts;
  /** The request went out — advance to the confirmation. */
  onSent: () => void;
  /** The token expired or was consumed elsewhere — show the link-expired state. */
  onTokenExpired: () => void;
};

/**
 * The single-match confirmation: the signup's domain resolved to one existing
 * Customer.io account, so we collect the requester's name and ask an admin to
 * add them. Org names are not shown (we identify the org by domain), and the
 * ambiguous multi-match case is handled upstream as a dead-end — this step
 * always has exactly one account to join.
 */
export function OrgPickerStep({ token, data, onSent, onTokenExpired }: OrgPickerStepProps) {
  const snackbars = useSnackbars();
  const accountId = data.account_id ?? '';

  const form = useZodForm({
    schema,
    defaultValues: { firstName: '', lastName: '' },
    // Validate on change so the submit button enables the moment both names
    // are filled.
    mode: 'onChange',
  });

  const sendRequest = useMutation({
    mutationFn: (values: z.infer<typeof schema>) =>
      createJoinRequest(token, {
        accountId,
        firstName: values.firstName.trim(),
        lastName: values.lastName.trim(),
      }),
  });

  const onSubmit = form.handleSubmit(async (values) => {
    // normalizeMatch only reports match="single" when there's an id to submit,
    // so this is unreachable — but a bare `return` here would resolve the submit
    // promise and unlock the form with nothing sent and nothing said, so route it
    // through the same error the user would see for any other failed send.
    if (accountId === '') {
      snackbars.create({
        variant: 'critical',
        id: 'joinRequest.create.error',
        message: "Your request couldn't be sent. Try again.",
      });
      return;
    }
    try {
      await sendRequest.mutateAsync(values);
      onSent();
    } catch (error) {
      // A 404 means the token expired or was consumed elsewhere → link-expired.
      if (error instanceof HydraUIAPIError && error.status === 404) {
        onTokenExpired();
        return;
      }
      // Any other failure surfaces as a transient error snackbar (Pluma forms
      // shouldn't show a standalone error banner). Swallowed here so the form's
      // submit promise doesn't reject into an unhandled rejection.
      snackbars.create({
        variant: 'critical',
        id: 'joinRequest.create.error',
        message: "Your request couldn't be sent. Try again.",
      });
    }
  });

  // Return the pending promise so Pluma <Form>'s autoLoading locks the fields
  // and guards Enter-key double submits while the request is in flight.
  return (
    <Form onSubmit={onSubmit}>
      <Stack gap="300">
        <Stack gap="100">
          <Heading level="1" size="lg" color="bold" data-test-join-request-heading>
            Join your team
          </Heading>
          <Paragraph color="subtle">
            Your organization already has a Customer.io account for <Text weight="semibold">{data.domain}</Text>. We'll
            notify an admin that <Text weight="semibold">{data.email}</Text> wants to join, and they'll set up your
            access.
          </Paragraph>
        </Stack>

        <Stack gap="200">
          <TextField
            {...form.registerPluma('firstName')}
            label="First name"
            autoComplete="given-name"
            data-test-join-request-first-name
          />
          <TextField
            {...form.registerPluma('lastName')}
            label="Last name"
            autoComplete="family-name"
            data-test-join-request-last-name
          />
        </Stack>

        <Stack gap="150">
          <Button
            type="submit"
            variant="primary"
            isDisabled={!form.formState.isValid}
            isLoading={sendRequest.isPending}
            data-test-join-request-submit
          >
            Request access
          </Button>
          <Box textAlign="center">
            <Text size="sm">
              <Link href={signupResumeHref(data.email)} data-test-join-request-continue-signup>
                Continue creating a new account instead
              </Link>
            </Text>
          </Box>
        </Stack>
      </Stack>
    </Form>
  );
}
